import { defineComponent as _defineComponent } from 'vue'
import RmpAutocomplete from "@rmp/core/components/fields/RmpAutocomplete.vue";
import { useI18n } from "vue-i18n-composable";
import { computed } from "vue";
import { DivisionsTreeNode } from "@rmp/core/utils/helpers";
import { normalizeDivisionIds } from "@rmp/core/utils/divisionScope";

type SelectItem = {
	value: string;
	title: string;
	rawTitle: string;
	type: DivisionsTreeNode["type"];
	depth: number;
	disabled: boolean;
};

interface Props {
	value?: string | string[] | undefined;
	items: DivisionsTreeNode[];
	loading?: boolean;
	onUpdateQuery?: (query: string) => void;
	noLabel?: boolean;
	disabled?: boolean;
	required?: boolean;
	multiple?: boolean;
	hideDetails?: boolean;
	width?: string;
	height?: string | number;
	branchSelectable?: boolean;
	// Залоченные подразделения: показываются с недоступным чекбоксом и всегда остаются в модели.
	lockedIds?: string[];
	label?: string;
	hint?: string;
	placeholder?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RmpDivisionsAutocomplete',
  props: {
    value: null,
    items: null,
    loading: { type: Boolean },
    onUpdateQuery: null,
    noLabel: { type: Boolean },
    disabled: { type: Boolean },
    required: { type: Boolean },
    multiple: { type: Boolean },
    hideDetails: { type: Boolean },
    width: null,
    height: null,
    branchSelectable: { type: Boolean },
    lockedIds: { default: () => [] },
    label: null,
    hint: null,
    placeholder: null
  },
  emits: ["input", "update:query"],
  setup(__props: any, { emit }) {

const props = __props as { value?: string | string[] | undefined, items: DivisionsTreeNode[], loading?: boolean, onUpdateQuery?: (query: string) => void, noLabel?: boolean, disabled?: boolean, required?: boolean, multiple?: boolean, hideDetails?: boolean, width?: string, height?: string | number, branchSelectable?: boolean, lockedIds: string[], label?: string, hint?: string, placeholder?: string };

const { tc } = useI18n();




const model = computed({
	get: () => {
		// Залоченные подразделения всегда считаются выбранными, даже если модель формы их ещё не содержит.
		if(props.multiple && props.lockedIds.length) {
			return normalizeDivisionIds([...(Array.isArray(props.value) ? props.value : []), ...props.lockedIds]);
		}
		return props.value ?? undefined;
	},
	set: (val) => {
		// Залоченные подразделения нельзя снять — всегда возвращаем их в модель.
		if(props.multiple && Array.isArray(val)) {
			emit("input", normalizeDivisionIds([...val, ...props.lockedIds]));
			return;
		}
		emit("input", val);
	}
});

function getNodeTitle(node: DivisionsTreeNode) {
	return node.data.division.name;
}

function flattenTree(nodes: DivisionsTreeNode[], depth = 0): SelectItem[] {
	const lockedSet = new Set(props.lockedIds);
	return nodes.flatMap((node) => {
		const rawTitle = getNodeTitle(node);
		
		const current: SelectItem = {
			value: node.id,
			rawTitle,
			title: rawTitle,
			type: node.type,
			depth,
			disabled: lockedSet.has(node.id)
		};
		
		const children = node.children?.length
			? flattenTree(node.children, depth + 1)
			: [];
		
		return [current, ...children];
	});
}

const items = computed<SelectItem[]>(() => flattenTree(props.items));

// Плейсхолдер показываем только когда ничего не выбрано, чтобы он не налезал на выбранные значения.
const hasSelection = computed(() => Array.isArray(props.value) ? props.value.length > 0 : Boolean(props.value));
const resolvedPlaceholder = computed(() => hasSelection.value
	? undefined
	: (props.placeholder ?? tc("fields.division.placeholder")));

// Возвращает признак залоченного элемента, чекбокс которого недоступен.
function isOptionDisabled(item: SelectItem): boolean {
	return item.disabled;
}

const onUpdateQuery = (query: string) => {
	props.onUpdateQuery?.(query);
	emit("update:query", query);
};

return { __sfc: true,tc, props, emit, model, getNodeTitle, flattenTree, items, hasSelection, resolvedPlaceholder, isOptionDisabled, onUpdateQuery, RmpAutocomplete }
}

})